import React, { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";
import { FileSignature, Download, FileSpreadsheet } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Read-only panel for legal/insurance follow-ups. Lists every signed waiver
 * with name, email, IP, timestamp, and a one-click PDF re-download.
 */
export default function SignedWaiversPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // waiver id being downloaded
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/signed-waivers");
        setItems(data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load signed waivers");
      }
    })();
  }, []);

  const downloadPdf = async (w) => {
    setBusy(w.id);
    try {
      // axios with responseType: 'blob' so the browser receives raw PDF bytes.
      const token = localStorage.getItem("gt_token");
      const res = await fetch(`${API}/admin/signed-waivers/${w.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GainTimeFitness-Waiver-${(w.email || "member").replace("@", "_at_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const token = localStorage.getItem("gt_token");
      const res = await fetch(`${API}/admin/signed-waivers.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `gain-time-signed-waivers-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${items.length} waivers exported`);
    } catch (e) {
      toast.error(e.message || "CSV export failed");
    } finally {
      setBusy(null);
    }
  };

  const visible = expanded ? items : items.slice(0, 5);

  return (
    <div className="mb-8 border border-zinc-800 bg-[#141414]" data-testid="signed-waivers-panel">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileSignature size={16} className="text-[#E5B43A]" />
          <h3 className="font-display text-lg">Signed Waivers</h3>
          <span className="text-xs text-zinc-500">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={busy === "csv" || items.length === 0}
            data-testid="export-waivers-csv"
            className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-700 px-2.5 py-1.5 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors disabled:opacity-40"
          >
            <FileSpreadsheet size={11} /> {busy === "csv" ? "..." : "Export CSV"}
          </button>
          {items.length > 5 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="toggle-waivers-expand"
              className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E5B43A] transition-colors"
            >
              {expanded ? "Show Less" : `Show All (${items.length})`}
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
              <th className="text-left px-5 py-2.5 font-bold">Name</th>
              <th className="text-left px-5 py-2.5 font-bold">Email</th>
              <th className="text-left px-5 py-2.5 font-bold hidden md:table-cell">Signed</th>
              <th className="text-left px-5 py-2.5 font-bold hidden lg:table-cell">IP</th>
              <th className="text-right px-5 py-2.5 font-bold">PDF</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500 text-xs">No signed waivers yet.</td></tr>
            ) : visible.map((w) => (
              <tr key={w.id} className="border-b border-zinc-900 last:border-0 hover:bg-[#1a1a1a]" data-testid={`waiver-row-${w.id}`}>
                <td className="px-5 py-3 font-semibold whitespace-nowrap">{w.name}</td>
                <td className="px-5 py-3 text-zinc-300 break-all">{w.email}</td>
                <td className="px-5 py-3 text-zinc-500 text-xs hidden md:table-cell whitespace-nowrap">{fmtDate(w.signed_at)}</td>
                <td className="px-5 py-3 text-zinc-500 text-xs hidden lg:table-cell font-mono">{w.ip}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => downloadPdf(w)}
                    disabled={busy === w.id}
                    data-testid={`download-waiver-${w.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-300 border border-emerald-400/30 px-2.5 py-1.5 hover:bg-emerald-400/10 transition-colors disabled:opacity-40"
                  >
                    <Download size={11} /> {busy === w.id ? "..." : "PDF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
